// PATH: src/hooks/useLearningHeartbeat.ts
import { useEffect, useRef } from 'react';
import { useLearningApi } from '../api/learning/learning';

/**
 * Intervalle entre deux signaux de présence pendant une leçon.
 *
 * DÉFINITION PROVISOIRE (D-13) : le « temps actif » (PED-09) est le nombre de
 * minutes distinctes portant au moins un battement. Cette constante est la
 * seule, côté front, qui fixe la cadence ; son pendant côté SQL est
 * `HEARTBEAT_MINUTE = 1`. Les changer séparément fausserait la mesure.
 */
export const HEARTBEAT_INTERVAL_MS = 60000;

type Timer =
    | { kind: 'timeout'; id: ReturnType<typeof setTimeout> }
    | { kind: 'interval'; id: ReturnType<typeof setInterval> };

/**
 * Émet un signal de présence toutes les `HEARTBEAT_INTERVAL_MS` tant que la
 * leçon `lessonId` est ouverte ET que la page est visible.
 *
 * Règles (lot 7) :
 * - rien n'est émis quand la page est cachée : un onglet en arrière-plan n'est
 *   pas du temps d'apprentissage ;
 * - aucune file d'attente, aucun rattrapage : un battement manqué (page cachée,
 *   réseau coupé) est une minute non active, c'est la définition même ;
 * - un seul minuteur à la fois : revenir sur la page ne double jamais la
 *   cadence, et le battement suivant respecte l'intervalle depuis le dernier
 *   envoyé ;
 * - les erreurs ne dérangent jamais l'élève (aucun message à l'écran) mais
 *   restent visibles en console.
 *
 * `lessonId` nul ou absent : aucune émission (leçon en chargement, non
 * accessible…).
 */
export const useLearningHeartbeat = (lessonId: number | null | undefined): void => {
    const { sendHeartbeat } = useLearningApi();

    // La fonction d'API change d'identité à chaque rendu (voir useLearningApi) :
    // on la lit par une ref pour ne pas relancer le minuteur à chaque rendu.
    const sendRef = useRef(sendHeartbeat);
    sendRef.current = sendHeartbeat;

    useEffect(() => {
        if (!lessonId) return undefined;

        let timer: Timer | null = null;
        let lastSentAt: number | null = null;

        const beat = () => {
            if (document.visibilityState !== 'visible') return;
            lastSentAt = Date.now();
            let eventId: string;
            try {
                eventId = globalThis.crypto.randomUUID();
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn('[heartbeat] impossible de générer un identifiant de battement', err);
                return;
            }
            sendRef.current({ event_id: eventId, lesson_id: lessonId }).catch((err: unknown) => {
                // eslint-disable-next-line no-console
                console.warn('[heartbeat] battement non transmis', err);
            });
        };

        const stop = () => {
            if (timer === null) return;
            if (timer.kind === 'timeout') clearTimeout(timer.id);
            else clearInterval(timer.id);
            timer = null;
        };

        const startInterval = () => {
            timer = { kind: 'interval', id: setInterval(beat, HEARTBEAT_INTERVAL_MS) };
        };

        const start = () => {
            if (timer !== null) return; // déjà en marche : jamais deux minuteurs
            const elapsed = lastSentAt === null ? HEARTBEAT_INTERVAL_MS : Date.now() - lastSentAt;
            if (elapsed >= HEARTBEAT_INTERVAL_MS) {
                beat();
                startInterval();
                return;
            }
            // Retour sur la page moins d'une minute après le dernier battement :
            // on reprend la cadence là où elle en était, sans battement anticipé.
            timer = {
                kind: 'timeout',
                id: setTimeout(() => {
                    beat();
                    startInterval();
                }, HEARTBEAT_INTERVAL_MS - elapsed),
            };
        };

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') start();
            else stop();
        };

        if (document.visibilityState === 'visible') start();
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            stop();
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [lessonId]);
};
